import React, { useEffect, useMemo, useState } from "react";
import "./UserRequests.css";
import Table from "../../../components/reusable-table/Table";
import Input from "../../../components/reusable-input/Input";
import { SearchIcon } from "../../../icons/reactIcons";
import UserRequestModal from "./userRequest-modal/UserRequestModal";
import {
  fetchUserRequests,
  refetchUserRequests,
  updateUserRequest,
} from "../../../redux/slices/userRequestSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { formatDateTime } from "../../../utils/formatDateTime";
import { formatMobileNumber } from "../../../utils/formatMobileNumber";
import Loader from "../../../components/custom-loader/Loader";

const STATUS_MAP: Record<string, "New Request" | "Accepted" | "Rejected"> = {
  pending: "New Request",
  accept: "Accepted",
  reject: "Rejected",
};

const commonColumns = [
  { header: "Name", accessor: "displayName" },
  { header: "Email", accessor: "email", className: "no-capitalize" },
  { header: "Mobile Number", accessor: "mobile" },
];

const newRequestColumns = [
  ...commonColumns,
  { header: "Date & Time", accessor: "dateTime" },
];

const acceptedColumns = [
  ...commonColumns,
  { header: "Accepted By", accessor: "acceptedInfo" },
];

const rejectedColumns = [
  ...commonColumns,
  { header: "Rejected By", accessor: "rejectedInfo" },
  { header: "Reason", accessor: "reason" },
];

const UserRequests: React.FC = () => {
  const dispatch = useAppDispatch();
  const { requests, loading } = useAppSelector((state) => state.userRequests);

  const [tab, setTab] = useState<"New Request" | "Accepted" | "Rejected">(
    "New Request",
  );
  const [search, setSearch] = useState("");
  const [modalType, setModalType] = useState<"accept" | "reject" | null>(null);
  const [modalData, setModalData] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchUserRequests());
  }, [dispatch]);

  const tableData = useMemo(() => {
    return requests.map((item) => {
      const uiStatus = STATUS_MAP[item.status] ?? "New Request";

      const row: any = {
        id: item.id,
        status: uiStatus,
        displayName: `${item.firstName} ${item.lastName}`,
        email: item.email,
        mobile: formatMobileNumber(item.mobileNumber),
        dateTime: item.createdAt ? formatDateTime(item.createdAt) : "-",
      };

      if (item.status === "accept") {
        row.acceptedInfo = (
          <div className="merged-cell">
            <div className="merged-label">{item.updater?.name || "-"}</div>
            <div className="merged-date">
              {item.statusUpdatedDate
                ? formatDateTime(item.statusUpdatedDate)
                : "-"}
            </div>
          </div>
        );
      }

      if (item.status === "reject") {
        row.rejectedInfo = (
          <div className="merged-cell">
            <div className="merged-label">{item.updater?.name || "-"}</div>
            <div className="merged-date">
              {item.statusUpdatedDate
                ? formatDateTime(item.statusUpdatedDate)
                : "-"}
            </div>
          </div>
        );
        row.reason = item.rejectReason || "-";
      }

      return row;
    });
  }, [requests]);

  const filteredData = useMemo(() => {
    return tableData.filter(
      (row) =>
        row.status === tab &&
        Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase()),
    );
  }, [tableData, tab, search]);

  const getColumns = () => {
    if (tab === "New Request") return newRequestColumns;
    if (tab === "Accepted") return acceptedColumns;
    return rejectedColumns;
  };

  const handleAccept = (row: any) => {
    setModalType("accept");
    setModalData(row);
  };

  const handleReject = (row: any) => {
    setModalType("reject");
    setModalData(row);
  };

  const handleConfirm = async (data: any, reason?: string) => {
    if (!modalType || !modalData) return;

    await dispatch(
      updateUserRequest({
        userId: data?.id,
        status: modalType,
        rejectReason: modalType === "reject" ? reason : undefined,
      }),
    );

    dispatch(refetchUserRequests());
    setModalType(null);
    setModalData(null);
  };

  const actions =
    tab === "New Request"
      ? [
          {
            label: "Accept",
            className: "accept",
            onClick: handleAccept,
          },
          {
            label: "Reject",
            className: "reject",
            onClick: handleReject,
          },
        ]
      : [];

  const counts = {
    "New Request": tableData.filter((d) => d.status === "New Request").length,
    Accepted: tableData.filter((d) => d.status === "Accepted").length,
    Rejected: tableData.filter((d) => d.status === "Rejected").length,
  };

  if (loading)
    return (
      <>
        <Loader />
      </>
    );

  return (
    <div className="user-requests">
      <div className="Common-header-area">
        <h1>User Request</h1>

        <div className="user-request-search">
          <Input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            search
            startIcon={<SearchIcon fontSize={"20px"} />}
            removeBottomMargin
          />
        </div>
      </div>
      <div className="user-request-actions">
        <div className="tabs">
          {Object.entries(counts).map(([label, count]) => (
            <button
              key={label}
              className={`tab-btn ${tab === label ? "active" : ""}`}
              onClick={() => setTab(label as any)}
            >
              {label}
              <span className="tab-count">{count}</span>
            </button>
          ))}
        </div>
      </div>

      <Table
        columns={getColumns()}
        data={filteredData}
        actions={actions}
        rowsPerPage={15}
      />

      {modalType && modalData && (
        <UserRequestModal
          type={modalType}
          data={modalData}
          onClose={() => setModalType(null)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

export default UserRequests;
